import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Tab,
  Tabs,
} from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download'
import TextIcon from '@mui/icons-material/TextFields'
import NumberIcon from '@mui/icons-material/Numbers'
import PhraseIcon from '@mui/icons-material/FormatQuote'
import { MovingMrLaterImage } from './MovingMrLaterImage';
import AudioPlayer from './AudioPlayer';
import Article from './Article';
import AdsenseAd from './AdsenseAd';
import ProgressBar from './ProgressBar';
import CopyLinkButton from "./CopyLinkButton";
import { pageSx, baseBoxSx, contentSx, adRailSx, adInlineSx } from '../styles/layout';

const MODE = Object.freeze({
    TEXT: 'text',
    NUMBER: 'number',
    PHRASE: 'phrase',
});
const defaultOptionsByMode = {
  "text": {
    quantity: "a-few",
    unit: "moments",
  },
  "number": {
    quantity: 2,
    unit: "hours",
  },
  "phrase": {
    phrase: "much-much-later",
  }
}
const phrasePostFix = "later";
const defaultMode = MODE.TEXT;

const getRandomIntInclusive = (min, max) => {
  min = Math.ceil(min); // Ensure min is an integer
  max = Math.floor(max); // Ensure max is an integer
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

const buildPhrase = (quantity, unit) => {
  return quantity + " " + unit + " " + phrasePostFix;
};

const unslugify = (slug) => {
  return String(slug).replaceAll("-", " ");
}

const singularizeUnit = (unit, quantity) => {
  if (parseInt(quantity) === 1 || quantity === "another") {
    if (unit.endsWith("ies")) {
      return unit.substring(0, unit.length - 3) + "y";
    } else if (unit.endsWith("s")) {
      return unit.substring(0, unit.length - 1)
    }
  }
  return unit;
}

const MainPage = () => {
  const theme = useTheme();

  // Load options lists with the defaults so a delay in loading these from the backend does not break things.

  // The quantity option is either a list of numbers or texts, so two arrays.
  const [quantityOptions, setQuantityOptions] = useState({
    number: [defaultOptionsByMode[MODE.NUMBER].quantity],
    text: [defaultOptionsByMode[MODE.TEXT].quantity]}
  );
  const [unitOptions, setUnitOptions] = useState([
    defaultOptionsByMode[MODE.NUMBER].unit,
    defaultOptionsByMode[MODE.TEXT].unit,
  ]);
  const [phraseOptions, setPhraseOptions] = useState([
    defaultOptionsByMode[MODE.PHRASE].phrase,
  ]);
  const [popularOptions, setPopularOptions] = useState([]);

  const [downloadUrl, setDownloadUrl] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);

  const audioBoxRef = useRef(null);

  const size = "large"
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const getMostPopularOptions = (mode) => {
    // mostPopularOptions does not have any entries for mode = phrase atm.
    if (mode !== MODE.PHRASE) {
      let mostPopularOptions = popularOptions.find((popularOptions) => {
        return popularOptions.type === mode;
      });

      if (mostPopularOptions) {
        return mostPopularOptions;
      }
    }
    return defaultOptionsByMode[mode];
  }

  const applyPopularOptions = (options) => {

    navigate(`/${encodeSlug(options)}`, { replace: false })

    audioBoxRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const changeMode = (e, newMode) => {
    navigate(`/${encodeSlug(getMostPopularOptions(newMode))}`, { replace: false });
  };

  const changeQuantity = (e) => {
    const newQuantity = e.target.value
    navigate(`/${encodeSlug({...options, quantity: newQuantity})}`, { replace: false });
  };

  const changeUnit = (e) => {
    const newUnit = e.target.value;
    navigate(`/${encodeSlug({...options, unit: newUnit})}`, { replace: false });
  };

  const changePhrase = (e) => {
    const newPhrase = e.target.value;
    // Shortcut because phrase === slug.
    navigate(`/${newPhrase}`, { replace: false });
  };

  const progressRef = useRef();

  const [listsLoaded, setListsLoaded] = useState(false);

  // Initial page load handler, fetch data from backend.
  useEffect(() => {
    const loadQuantityOptions = async (e) => {
      const response = await axios.get(`${baseUrl}/num-units-options`);

      if ("error" in response.data) {
        // ...
      } else {
        setQuantityOptions(response.data)
      }
    };
    const loadUnitOptions = async (e) => {
      const response = await axios.get(`${baseUrl}/unit-options`);

      if ("error" in response.data) {
        // ...
      } else {
        setUnitOptions(response.data)
      }
    };
    const loadPhraseOptions = async (e) => {
      const response = await axios.get(`${baseUrl}/phrase-options`);

      if ("error" in response.data) {
        // ...
      } else {
        setPhraseOptions(response.data)
      }
    };
    const loadPopularPhrases = async (e) => {
      const response = await axios.get(`${baseUrl}/popular-phrases`);

      if ("error" in response.data) {
        // ...
      } else {
        setPopularOptions(response.data);
      }
    };

    Promise.all([
      loadQuantityOptions(),
      loadUnitOptions(),
      loadPhraseOptions(),
      loadPopularPhrases(),
    ]).then(() => {
      setListsLoaded(true);
    })
  }, []);

  /**
   * Navigation and loading by slug
   */

  const decodeSlug = (slug) => {
    let options = getMostPopularOptions(defaultMode);
    let isCanonical = false;

    if (slug) {
      if (phraseOptions.includes(slug)) {
        options = { phrase: slug };
        isCanonical = true;
      } else {
        const slugParts = slug.split('-');

        // Pop off the 'later' part.
        if (slugParts.pop() === phrasePostFix) {
          let unit = slugParts.pop();
          let quantity = slugParts.length === 1 ? slugParts.pop() : slugParts.join('-');
          let mode = MODE.TEXT;

          if (!Number.isNaN(parseInt(quantity))) {
            quantity = parseInt(quantity);
            mode = MODE.NUMBER;
          }
          if (quantityOptions[mode].includes(quantity) && unitOptions.includes(unit)) {
            options = {
              quantity,
              unit,
            };
            isCanonical = true;
          }
        }
      }
    }
    return { options, isCanonical };
  }

  const encodeSlug = (options) => {
    if (options.phrase) {
      return options.phrase;
    } else {
      return [options.quantity, options.unit, phrasePostFix].join('-');
    }
  }

  const { slug } = useParams();
  const navigate = useNavigate();

  const { options, isCanonical } = useMemo(() => {
    // Though the default options may not be canonical, we can't validate yet, so we don't 'judge'.
    if (!listsLoaded) {
      return { options: getMostPopularOptions(defaultMode), isCanonical: true };
    }
    return decodeSlug(slug);
  }, [slug, listsLoaded]);

  useEffect(() => {
    if (!listsLoaded || !options) return;

    if (!isCanonical) {
      navigate(`/${encodeSlug(options)}`, { replace: true });
    }
  }, [isCanonical, listsLoaded, options, navigate]);

  const quantityType = useMemo(() => {
    if (options.quantity) {
      if (Number.isNaN(parseInt(options.quantity))) {
        return 'text';
      } else {
        return 'number';
      }
    }
    return null;
  }, [options.quantity]);

  const mode = useMemo(() => {
    if (options.phrase) {
      return 'phrase';
    } else {
      return quantityType ?? 'text';
    }
  }, [options.phrase, quantityType]);

  // This gets called when the progress bar is completed
  const progressBarComplete = (response) => {
    if ("error" in response.data) {
    } else {
      setDownloadUrl(response.data.download_url);
      setStreamUrl(response.data.stream_url);
    }
  }

  // ProgressBarStart, technically
  useEffect(() => {
    setDownloadUrl(null);
    setStreamUrl(null);
    // Guard on readiness, as in, what dependencies need to be ready.
    if (slug != null) {
      // What action should be trigged (fake or real)?
      // TODO: Instead of just popularPhrases, we'll add a cache where popularPhrases is loaded initially, and others, fetched before are added.
      let cachedPhraseObject = popularOptions.find((p) => p.phrase === slug);
      if (!cachedPhraseObject) {
        let body = {
          phrase: slug,
        };
        progressRef.current.startRun({
          type: "real",
          url: `${baseUrl}/process`,
          payload: body,
        });
      } else {
        progressRef.current.startRun({
          type: "fake",
          fakeData: {
            stream_url: cachedPhraseObject.stream_url,
            download_url: cachedPhraseObject.download_url,
          },
        });
      }
    }
  }, [slug]);

  return (
    <Box
      sx={pageSx}
    >
      <Box sx={adRailSx}>
        <AdsenseAd dataAdSlot="6568505948" format="vertical"></AdsenseAd>
      </Box>
      <Box sx={contentSx}>
        <Box
          sx={baseBoxSx}
        >
          <Typography variant="h3" gutterBottom>
            Mr. Later
          </Typography>
          <Typography gutterBottom>
            Create and download Mp3's of <strong>time-lapse phrases</strong>, with a funny French accent, you can use in your video or audio content. <strong>Free!</strong>
          </Typography>
          <Typography>
            This tool lets you quickly generate custom SpongeBob-inspired, French-accented transitions and drop them straight into your edits.
          </Typography>
          <Typography>
            <a href="#origins">Learn more about the origins</a>
          </Typography>
        </Box>
        <Box sx={adInlineSx}>
          <AdsenseAd dataAdSlot="6568505948" format="auto"></AdsenseAd>
        </Box>
        <Box
          ref={audioBoxRef}
          sx={baseBoxSx}
        >
          <Typography variant="h5" gutterBottom>
            Say it with a French accent...
          </Typography>

          <Typography gutterBottom>
            You have { (quantityOptions.number.length + quantityOptions.text.length) * unitOptions.length + phraseOptions.length } options; choose wisely...
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              height: '100%',
              justifyContent: 'center'
            }}
          >
              <MovingMrLaterImage/>
          </Box>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              marginBottom: 2
            }}
          >
            <Tabs
              value={mode}
              onChange={changeMode}
              variant="fullWidth"
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab icon={<TextIcon />} value="text" />
              <Tab icon={<NumberIcon />} value="number" />
              <Tab icon={<PhraseIcon />} value="phrase" />
            </Tabs>
          </Box>
          <Box
            component="form"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              marginBottom: 2
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              {mode !== 'phrase' && (
                <>
                  <FormControl fullWidth>
                    <InputLabel sx={{ textTransform: 'capitalize' }}>
                      {mode}
                    </InputLabel>
                    <Select
                      value={options.quantity}
                      renderValue={unslugify}
                      label={mode}
                      onChange={changeQuantity}
                      sx={{
                        textTransform: 'capitalize',
                        '& .MuiSelect-select': {
                          textAlign: 'left',
                        },
                      }}
                      size={size}
                    >
                      {quantityOptions[mode].map((quantityOption) => (
                        <MenuItem sx={{ textTransform: 'capitalize' }} key={quantityOption} value={quantityOption}>
                          {unslugify(quantityOption)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Unit</InputLabel>
                    <Select
                      value={options.unit}
                      renderValue={unit => singularizeUnit(unit, options.quantity)}
                      label="Unit"
                      onChange={changeUnit}
                      sx={{
                        textTransform: 'capitalize',
                        '& .MuiSelect-select': {
                          textAlign: 'left',
                        },
                      }}
                      size={size}
                    >
                      {unitOptions.map((unitOption) => (
                        <MenuItem sx={{ textTransform: 'capitalize' }} key={unitOption} value={unitOption}>
                          {singularizeUnit(unitOption, options.quantity)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    value={phrasePostFix + "..."}
                    disabled
                    fullWidth
                    size={size}
                    slotProps={{
                      htmlInput: {
                        sx: {
                          textTransform: 'capitalize',
                        },
                      },
                    }}
                  />
                </>
              )}

              {mode === MODE.PHRASE && (
                <FormControl fullWidth>
                  <InputLabel>Phrase</InputLabel>
                  <Select
                    value={options.phrase}
                    renderValue={unslugify}
                    label="Phrase"
                    onChange={changePhrase}
                    sx={{
                      textTransform: 'capitalize',
                      '& .MuiSelect-select': {
                        textAlign: 'left',
                      },
                    }}
                    size={size}
                  >
                    {phraseOptions.map((phraseOption) => (
                      <MenuItem sx={{ textTransform: 'capitalize' }} key={phraseOption} value={phraseOption}>
                        {unslugify(phraseOption)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Box>

          <ProgressBar
            ref={progressRef}
            onComplete={(response) => progressBarComplete(response)}
          />

          <Box mt={2}>
            <AudioPlayer download={downloadUrl && baseUrl + downloadUrl} stream={streamUrl && baseUrl + streamUrl}/>
          </Box>
        </Box>
        <Box sx={adInlineSx}>
          <AdsenseAd dataAdSlot="6568505948" format="auto"></AdsenseAd>
        </Box>
        <Box
          sx={baseBoxSx}
        >
          <Typography variant="h5" gutterBottom>
            Popular Phrases
          </Typography>
          <List dense={false}>
            {popularOptions.map((options, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <Box
                    sx={{
                      background: `linear-gradient(to right, transparent 0, ${baseBoxSx().bgcolor} 8px)`,
                    }}
                  >
                    <IconButton
                      onClick={() => applyPopularOptions(options)}
                      disabled={options.stream_url === streamUrl}
                    >
                      <PlayArrowIcon />
                    </IconButton>
                    <IconButton
                      href={`${baseUrl}${options.download_url}`}
                      download
                    >
                      <DownloadIcon />
                    </IconButton>
                    <CopyLinkButton url={`${window.location.origin}/${encodeSlug(options)}`} />
                  </Box>
                }
              >
                <ListItemAvatar
                  sx={{
                      minWidth: {xs: '35px'},
                      scale: {xs: '65%'},
                  }}
                >
                  <Avatar
                    alt="Mr. Later"
                    src="/static/images/apple-touch-icon.png"
                    sx={{
                      filter: "hue-rotate(" + getRandomIntInclusive(0, 360) + "deg)",
                      rotate: getRandomIntInclusive(0, 360) + "deg",
                    }}
                />
                </ListItemAvatar>
                <ListItemText
                  sx={{ textTransform: 'capitalize' }}
                  primary={ buildPhrase(options.quantity, singularizeUnit(options.unit, options.quantity)) + "..." }
                />
              </ListItem>
            ))}
          </List>
        </Box>
        <Box sx={adInlineSx}>
          <AdsenseAd dataAdSlot="6568505948" format="auto"></AdsenseAd>
        </Box>
        <Box
          id="origins"
          sx={{
            ...baseBoxSx(),
            mb: 4,
          }}
        >
          <Article/>
        </Box>
      </Box>
      <Box sx={adRailSx}>
        {/* <AdsenseAd dataAdSlot="6568505948" format="vertical"></AdsenseAd> */}
      </Box>
    </Box>
  );
};

export default MainPage;
